import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

const PAGE_SIZE = 50;

export interface ScootFilters {
  minFollowers?: number;
  maxFollowers?: number;
  minEngagement?: number;
  maxEngagement?: number;
  minAvgViews?: number;
  maxAvgViews?: number;
}

export function useScootQuery(searchQuery: string, filters?: ScootFilters) {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef(0);
  const loadingRef = useRef(false);

  const fetchPage = useCallback(async (pageNum: number, append: boolean) => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    if (pageNum === 0) {
      setLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    try {
      const from = pageNum * PAGE_SIZE;

      const { data, error: queryError } = await supabase.rpc('search_influencers', {
        search_term: searchQuery || '',
        platform_filter: 'all',
        category_filter: 'all',
        min_followers: filters?.minFollowers || 0,
        max_followers: filters?.maxFollowers || 10000000,
        min_engagement: filters?.minEngagement || 0,
        max_engagement: filters?.maxEngagement || 100,
        min_avg_views: filters?.minAvgViews || null,
        max_avg_views: filters?.maxAvgViews || null,
        page_offset: from,
        page_limit: PAGE_SIZE
      });

      if (queryError) {
        console.error('Error fetching influencers:', queryError);
        throw new Error('Erreur lors de la recherche');
      }

      const newData = data || [];

      if (newData.length < PAGE_SIZE) {
        setHasMore(false);
      }

      if (append) {
        setResults(prev => {
          const existingIds = new Set(prev.map(r => r.id));
          const unique = newData.filter(r => !existingIds.has(r.id));
          return [...prev, ...unique];
        });
      } else {
        setResults(newData);
      }

      pageRef.current = pageNum;
    } catch (err) {
      console.error('Error fetching influencers:', err);
      setError('Erreur lors de la recherche');
      if (!append) setResults([]);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      loadingRef.current = false;
    }
  }, [searchQuery, filters]);

  useEffect(() => {
    pageRef.current = 0;
    setHasMore(true);
    fetchPage(0, false);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) {
      fetchPage(pageRef.current + 1, true);
    }
  }, [fetchPage, hasMore]);

  return { results, loading, isLoadingMore, hasMore, error, loadMore };
}
